import React, { useState, useEffect } from 'react';

export default function GameTimer({ gameTimer, preparationPeriod, isChatExpanded, winningGoal, containerStyle = {} }) {
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [showGoal, setShowGoal] = useState(false);

  // Update current time every second for local calculation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Toggle between timer and goal every 10 seconds
  useEffect(() => {
    if (!winningGoal) return;
    
    const interval = setInterval(() => {
      setShowGoal(prev => !prev);
    }, 10000);

    return () => clearInterval(interval);
  }, [winningGoal]);

  if (gameTimer?.isCompleted) return null;

  // Calculate remaining time locally
  const calculateRemainingTime = () => {
    if (preparationPeriod?.isInPreparation && preparationPeriod.endTime) {
      const endTime = new Date(preparationPeriod.endTime).getTime();
      const remaining = Math.max(0, Math.floor((endTime - currentTime) / 1000));
      return { time: remaining, label: "Preparation", isPreparation: true };
    } else if (gameTimer?.endTime) {
      const endTime = new Date(gameTimer.endTime).getTime();
      const remaining = Math.max(0, Math.floor((endTime - currentTime) / 1000));
      return { time: remaining, label: "Game Time", isPreparation: false };
    }
    return null;
  };

  const timerData = calculateRemainingTime();
  if (!timerData) return null;

  const { time: timeToShow, label: timerLabel, isPreparation } = timerData;

  const hours = Math.floor(timeToShow / 3600);
  const minutes = Math.floor((timeToShow % 3600) / 60);
  const seconds = timeToShow % 60;

  const formatTime = (time) => time.toString().padStart(2, '0');

  // Color changes based on remaining time and preparation status
  const getTimerColor = () => {
    if (isPreparation) return '#FFD700'; // Gold color for preparation period
    if (timeToShow <= 300) return '#FF0000'; // Bright red for last 5 minutes
    if (timeToShow <= 900) return '#FF6600'; // Bright orange for last 15 minutes
    if (timeToShow <= 1800) return '#FFAA00'; // Bright yellow-orange for last 30 minutes
    return '#00FF00'; // Bright green for normal time
  };

  const getTimerBackground = () => {
    return 'rgba(0, 0, 0, 0.95)';
  };

  const getBorderColor = () => {
    return getTimerColor();
  };

  const getTextShadow = () => {
    return `0 0 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.6), 1px 1px 2px rgba(0,0,0,1)`;
  };

  return (
    <div style={{
      position: 'absolute',
      bottom: '10px',
      left: isChatExpanded ? '340px' : '180px',
      background: getTimerBackground(),
      color: getTimerColor(),
      borderRadius: '8px',
      padding: '10px 15px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '1.1em',
      fontWeight: 'bold',
      border: `2px solid ${getBorderColor()}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      width: '155px',
      height: '44px',
      boxSizing: 'border-box',
      justifyContent: 'center',
      boxShadow: `
        0 4px 12px rgba(0,0,0,0.5),
        0 0 20px rgba(${getTimerColor().slice(1).match(/.{2}/g).map(hex => parseInt(hex, 16)).join(', ')}, 0.3),
        inset 0 1px 0 rgba(255,255,255,0.1)
      `,
      transition: 'all 0.3s ease',
      textShadow: getTextShadow(),
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      filter: `drop-shadow(0 0 6px ${getTimerColor()}40)`,
      zIndex: 1001,
      overflow: 'hidden',
      ...containerStyle
    }}>
      {/* Timer Display */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        position: 'absolute',
        transition: 'transform 1s ease-in-out',
        transform: showGoal && winningGoal ? 'translateX(-200px)' : 'translateX(0)',
        whiteSpace: 'nowrap'
      }}>
        <span style={{ 
          fontSize: '1.2em',
          filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))'
        }}>
          {isPreparation ? '🔍' : '⏱️'}
        </span>
        <span style={{
          fontFamily: 'monospace',
          letterSpacing: '1px',
          filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))'
        }}>
          {isPreparation && hours === 0 ? 
            `${formatTime(minutes)}:${formatTime(seconds)}` :
            `${formatTime(hours)}:${formatTime(minutes)}:${formatTime(seconds)}`
          }
        </span>
      </div>

      {/* Goal Display */}
      {winningGoal && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          position: 'absolute',
          transition: 'transform 1s ease-in-out',
          transform: showGoal ? 'translateX(0)' : 'translateX(200px)',
          whiteSpace: 'nowrap',
          color: '#FFD700'
        }}>
          <span style={{ 
            fontSize: '1.2em',
            filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))'
          }}>
            🏆
          </span>
          <span style={{
            fontFamily: 'monospace',
            letterSpacing: '1px',
            filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.8))'
          }}>
            ${winningGoal.toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}